import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { BaseService } from '@/core/api/base-service';
import { ApiConfiguration } from '@/core/api/api-configuration';
import { MedicoPendenciaResponse } from './medico-pendencia.model';
import { MedicoRneVencendoResponse } from './medico-rne-vencendo.model';
import { Page } from '@/shared/models/page.model';
import { environment } from '@env/environment';
import { SICOM_PATHS } from 'src/sicom-paths';

@Injectable({ providedIn: 'root' })
export class MedicoPendenciaService extends BaseService {
    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    readonly PATH = SICOM_PATHS.registro.medico.pendencia;

    private criarParams(uf?: string | null, page?: number, size?: number): HttpParams {
        let params = new HttpParams();
        if (uf) {
            params = params.set('uf', uf);
        }
        if (page !== undefined && page !== null) {
            params = params.set('page', String(page));
        }
        if (size !== undefined && size !== null) {
            params = params.set('size', String(size));
        }

        return params;
    }

    listarPorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta`, { params, context });
    }

    totalPorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/total`, { params, context });
    }

    listarCpfInvalidosOuNulosPorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/cpf-invalido-ou-nulo`, { params, context });
    }

    totalCpfInvalidosOuNulosPorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/cpf-invalido-ou-nulo/total`, { params, context });
    }

    listarGeneroNaoInformadoPorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/genero-nao-informado`, { params, context });
    }

    totalGeneroNaoInformadoPorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/genero-nao-informado/total`, { params, context });
    }

    listarDivergenciaDataNascimentoPorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/divergencia-data-nascimento`, { params, context });
    }

    totalDivergenciaDataNascimentoPorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/divergencia-data-nascimento/total`, { params, context });
    }

    listarEspecialidadePorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/especialidade`, { params, context });
    }

    totalEspecialidadePorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/especialidade/total`, { params, context });
    }

    listarFormaturaAntesCriacaoFaculdadePorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/formatura-antes-criacao-faculdade`, { params, context });
    }

    totalFormaturaAntesCriacaoFaculdadePorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/formatura-antes-criacao-faculdade/total`, { params, context });
    }

    listarFormaturaMaiorQueInscricaoPorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/formatura-maior-que-inscricao`, { params, context });
    }

    totalFormaturaMaiorQueInscricaoPorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/formatura-maior-que-inscricao/total`, { params, context });
    }

    listarEnderecoCorrespondenciaPorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/endereco-correspondencia`, { params, context });
    }

    totalEnderecoCorrespondenciaPorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/endereco-correspondencia/total`, { params, context });
    }

    listarPrazoDiplomaVencidoPorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/prazo-diploma-vencido`, { params, context });
    }

    totalPrazoDiplomaVencidoPorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/prazo-diploma-vencido/total`, { params, context });
    }

    listarCancelamentoComDebitoPorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/cancelamento-com-debito`, { params, context });
    }

    totalCancelamentoComDebitoPorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/cancelamento-com-debito/total`, { params, context });
    }

    listarInscritosAcima80AnosPorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/inscritos-acima-80-anos`, { params, context });
    }

    totalInscritosAcima80AnosPorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/inscritos-acima-80-anos/total`, { params, context });
    }

    listarRneVencendoEm30DiasPorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoRneVencendoResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoRneVencendoResponse>>(`${this.PATH}/consulta/rne-vencendo-30-dias`, { params, context });
    }

    totalRneVencendoEm30DiasPorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/rne-vencendo-30-dias/total`, { params, context });
    }

    listarRneVencidoPorUf(uf?: string | null, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoRneVencendoResponse>> {
        const params = this.criarParams(uf, page, size);
        return this.http.get<Page<MedicoRneVencendoResponse>>(`${this.PATH}/consulta/rne-vencido`, { params, context });
    }

    totalRneVencidoPorUf(uf?: string | null, context?: HttpContext): Observable<number> {
        const params = this.criarParams(uf);
        return this.http.get<number>(`${this.PATH}/consulta/rne-vencido/total`, { params, context });
    }
}
